import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import {
  formatPeriode,
  getAggregatesForPeriod,
  getRowPeriode,
} from "./dashboardPeriodUtils";
import { useI18n } from "../i18n/I18nContext";
import { PLIS_TO_MM_LABEL, PLIS_RECONNUS } from "../utils/empilementPopulations";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const COLORS = {
  moe: "#378ADD",
  moeDark: "#185FA5",
  moy: "#639922",
  bar: "#3b5f7a",
  barEdge: "#1e3a50",
  yellow: "#E6FF3E",
  gray: "#94a3b8",
  red: "#f87171",
};

const POPULATION_KEYS = ["global", ...PLIS_RECONNUS.map(String)];
const POPULATION_LABELS = {
  global: "GLOBAL",
  ...Object.fromEntries(
    PLIS_RECONNUS.map((n) => [String(n), PLIS_TO_MM_LABEL[n]])
  ),
};

function ChartTooltip({ active, payload, label, showE005 = true }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload
        .filter((p) => showE005 || p.dataKey !== "e005")
        .map((p) => (
        <div key={p.dataKey} style={{ color: p.color, fontSize: 12 }}>
          {p.name}: {p.value ?? "N/A"}
          {p.dataKey === "e005" || p.dataKey === "moyenne" ? " GPa" : ""}
        </div>
      ))}
    </div>
  );
}

function BreakdownDetail({ row, periodMode }) {
  const bd = row.breakdown;
  const periodLabel = formatPeriode(getRowPeriode(row), periodMode);
  if (!bd) return null;

  if (bd.e005 == null) {
    return (
      <div style={S.detailBox}>
        <div style={S.detailTitle}>E0,05 (EN 14358) — {periodLabel}</div>
        <div style={{ color: COLORS.gray, fontSize: 12 }}>
          n = {bd.n} &lt; 2 → écart-type indéfini, E0,05 = N/A
        </div>
      </div>
    );
  }

  return (
    <div style={S.detailBox}>
      <div style={S.detailTitle}>E0,05 (EN 14358, loi normale) — {periodLabel}</div>
      <div style={S.detailLine}>x_j retenus (n={bd.n}) = [{bd.xs.map((v) => v.toFixed(3)).join(", ")}]</div>
      <div style={S.detailLine}>E_moy = {bd.e_moy} GPa</div>
      <div style={S.detailLine}>s (ddof=1) = {bd.s_raw} GPa</div>
      <div style={S.detailLine}>
        plancher 0.05×E = {bd.floor} → s retenu = {bd.s} GPa
      </div>
      <div style={S.detailLine}>k_s({bd.n}) = {bd.ks}</div>
      <div style={{ ...S.detailLine, color: COLORS.moe, fontWeight: 700 }}>
        E0,05 = {bd.e_moy} − {bd.ks} × {bd.s} = {bd.e005} GPa
      </div>
      <div style={S.detailLine}>Moyenne des moyennes = {row.moyenne ?? "N/A"} GPa</div>
    </div>
  );
}

export default function DashboardMoeSection({ atTop = false, externalData = null }) {
  const { t, ui } = useI18n();

  const [loading, setLoading] = useState(!externalData);
  const [error, setError] = useState("");
  const [data, setData] = useState(externalData);

  useEffect(() => {
    if (externalData) {
      setData(externalData);
      setLoading(false);
      setError("");
      return;
    }
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch("/api/dashboard/moe");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.status === "error") throw new Error(json.message || "Erreur API");
        setData(json);
      } catch (e) {
        setError(e.message || "Erreur chargement MOE");
      } finally {
        setLoading(false);
      }
    })();
  }, [externalData]);

  const [population, setPopulation] = useState("global");
  const [periodMode, setPeriodMode] = useState("month");
  const [expandedPeriod, setExpandedPeriod] = useState(null);

  const { rows, weekUnavailable } = useMemo(() => {
    const { rows: r, weekUnavailable: wu } = getAggregatesForPeriod(data, population, periodMode);
    return { rows: r, weekUnavailable: wu };
  }, [data, population, periodMode]);

  const periodLabelCol = periodMode === "week" ? "Semaine" : "Mois";
  const chartTitle = periodMode === "week" ? "Graphique hebdomadaire" : "Graphique mensuel";
  const tableTitle = periodMode === "week" ? "Récapitulatif hebdomadaire" : "Récapitulatif mensuel";
  const showE005 = periodMode === "month";

  const tableHeaders = showE005
    ? [periodLabelCol, "E0,05 (GPa)", "Moyenne (GPa)", "nb Stacks Flexion", "nb Stacks Produits", ""]
    : [periodLabelCol, "Moyenne (GPa)", "nb Stacks Flexion", "nb Stacks Produits", ""];

  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        periode: formatPeriode(getRowPeriode(r), periodMode),
        n_produits: r.n_produits,
        e005: r.e005,
        moyenne: r.moyenne,
      })),
    [rows, periodMode]
  );

  const populationLabel = data?.populations?.[population] || population;

  const sectionStyle = atTop ? S.sectionFirst : S.section;

  if (loading) {
    return (
      <div style={sectionStyle}>
        <div style={{ color: COLORS.yellow, padding: 24 }}>Chargement MOE…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={sectionStyle}>
        <div style={{ color: COLORS.red, padding: 24 }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Suivi MOE{showE005 ? " — E0,05" : ""}</div>
          <div style={S.subtitle}>
            {showE005
              ? `Valeur caractéristique EN 14358 · éprouvettes < ${data?.params?.moe_min_mpa} MPa exclues`
              : "Suivi hebdomadaire · moyenne des moyennes uniquement (E0,05 réservé au mensuel)"}
          </div>
        </div>
      </div>

      <div style={S.tabRow}>
        <button
          type="button"
          style={{ ...S.tab, ...(periodMode === "month" ? S.tabActive : {}) }}
          onClick={() => { setPeriodMode("month"); setExpandedPeriod(null); }}
        >
          Par mois
        </button>
        <button
          type="button"
          style={{ ...S.tab, ...(periodMode === "week" ? S.tabActive : {}) }}
          onClick={() => { setPeriodMode("week"); setExpandedPeriod(null); }}
        >
          Par semaine
        </button>
      </div>

      <div style={S.tabRow}>
        {POPULATION_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            style={{
              ...S.tab,
              ...(population === key ? S.tabActive : {}),
            }}
            onClick={() => {
              setPopulation(key);
              setExpandedPeriod(null);
            }}
          >
            {POPULATION_LABELS[key]}
          </button>
        ))}
      </div>

      <div style={{ color: COLORS.gray, fontSize: 12, marginBottom: 16 }}>{populationLabel}</div>

      {weekUnavailable && (
        <div style={{ ...S.card, borderColor: COLORS.red, marginBottom: 16 }}>
          <div style={{ color: COLORS.red, fontSize: 13 }}>
            Données hebdomadaires indisponibles. Redémarrez le backend Flask pour charger la dernière version.
          </div>
        </div>
      )}

      {chartData.length === 0 ? (
        <div style={S.card}>
          <div style={{ color: COLORS.gray }}>Aucune donnée pour cette population.</div>
        </div>
      ) : (
        <>
          <div style={S.card}>
            <div style={S.cardTitle}>{chartTitle}</div>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="periode" tick={{ fill: COLORS.gray, fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: COLORS.gray, fontSize: 11 }}
                  allowDecimals={false}
                  label={{ value: "Stacks produits", angle: -90, position: "insideLeft", fill: COLORS.gray, fontSize: 11 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: COLORS.moeDark, fontSize: 11 }}
                  label={{ value: "MOE (GPa)", angle: 90, position: "insideRight", fill: COLORS.moeDark, fontSize: 11 }}
                />
                <Tooltip content={<ChartTooltip showE005={showE005} />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="n_produits"
                  name="Stacks produits"
                  fill={COLORS.bar}
                  stroke={COLORS.barEdge}
                  radius={[4, 4, 0, 0]}
                />
                {showE005 && (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="e005"
                    name="Valeur carac. MOE E0,05"
                    stroke={COLORS.moe}
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: COLORS.moe }}
                    connectNulls={false}
                  />
                )}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="moyenne"
                  name="Moyenne des moyennes"
                  stroke={COLORS.moy}
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: COLORS.moy }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>{tableTitle}</div>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {tableHeaders.map((h) => (
                      <th key={h || "action"} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const rowPeriode = getRowPeriode(row);
                    return (
                    <React.Fragment key={rowPeriode}>
                      <tr style={S.tr}>
                        <td style={S.td}>{formatPeriode(rowPeriode, periodMode)}</td>
                        {showE005 && (
                          <td style={{ ...S.td, color: COLORS.moe, fontWeight: 700 }}>
                            {row.e005 != null ? row.e005 : "N/A (n<2)"}
                          </td>
                        )}
                        <td style={{ ...S.td, color: COLORS.moy }}>
                          {row.moyenne ?? "N/A"}
                        </td>
                        <td style={S.td}>{row.n_panels}</td>
                        <td style={S.td}>{row.n_produits}</td>
                        <td style={S.td}>
                          <button
                            type="button"
                            style={S.detailBtn}
                            onClick={() =>
                              setExpandedPeriod((p) => (p === rowPeriode ? null : rowPeriode))
                            }
                          >
                            {expandedPeriod === rowPeriode ? "Masquer" : "Détail"}
                          </button>
                        </td>
                      </tr>
                      {expandedPeriod === rowPeriode && (
                        <tr>
                          <td colSpan={tableHeaders.length} style={{ padding: "12px 14px", background: "rgba(0,0,0,0.2)" }}>
                            {showE005 && <BreakdownDetail row={row} periodMode={periodMode} />}
                            <div style={{ marginTop: showE005 ? 12 : 0 }}>
                              <div style={S.detailTitle}>
                                Stacks de la période ({row.stacks?.length || 0})
                              </div>
                              {(row.stacks || []).map((d) => {
                                const excl = d.n_rows - d.n_valid;
                                return (
                                  <div key={d.stack} style={S.stackLine}>
                                    <span style={{ color: COLORS.yellow, fontWeight: 700 }}>{d.stack}</span>
                                    {" · "}
                                    {d.n_rows} éprouvette(s) → [{d.vals?.length ? d.vals.map((v) => v.toFixed(3)).join(", ") : "aucune"}] GPa
                                    {" → "}
                                    x_j = {d.x_j != null ? d.x_j.toFixed(3) : "N/A"}
                                    {excl > 0 && (
                                      <span style={{ color: COLORS.red }}>
                                        {" "}({excl} &lt; {data?.params?.moe_min_mpa} MPa exclue(s))
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  sectionFirst: {
    marginTop: 0,
    paddingTop: 0,
    marginBottom: 0,
    borderTop: "none",
  },
  section: {
    marginTop: 32,
    paddingTop: 32,
    borderTop: "1px solid #1e3a50",
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: 900,
    color: "#fff",
    letterSpacing: "0.04em",
  },
  subtitle: {
    fontSize: 12,
    color: COLORS.gray,
    marginTop: 4,
  },
  tabRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  tab: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: COLORS.gray,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  tabActive: {
    borderColor: COLORS.yellow,
    color: COLORS.yellow,
    background: "rgba(230,255,62,0.08)",
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.gray,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 16,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    padding: "10px 14px",
    textAlign: "left",
    color: COLORS.gray,
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: 10,
    letterSpacing: "0.06em",
    borderBottom: "1px solid #1e3a50",
  },
  tr: {
    borderBottom: "1px solid #1e3a50",
  },
  td: {
    padding: "10px 14px",
    color: "#e2e8f0",
  },
  detailBtn: {
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.06)",
    color: COLORS.yellow,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  detailBox: {
    marginBottom: 8,
  },
  detailTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.yellow,
    marginBottom: 8,
  },
  detailLine: {
    fontSize: 12,
    color: "#cbd5e1",
    marginBottom: 4,
    fontFamily: "monospace",
  },
  stackLine: {
    fontSize: 11,
    color: "#94a3b8",
    marginBottom: 4,
    fontFamily: "monospace",
  },
  tooltip: {
    background: "#071e2f",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "10px 14px",
  },
};
